import { IAccountConciliation, INsuVarDetails } from "@/@types/deposit-conference";
import { useFetchNsuVarDetailsMutation } from "@/services/conciliation/nsu";
import { Alert, AlertDescription } from "components/ui/alert";
import { Button } from "components/ui/button";
import { toast } from "components/ui/use-toast";
import { format } from "date-fns";
import { AlertCircle, Calendar, Loader2, Search, User } from "lucide-react";
import React, { useState } from "react";

import { MORE_INFO_LABELS, MORE_INFO_MESSAGES } from "../utils/messages";

interface NsuMoreInfoContentProps {
  nsu: IAccountConciliation;
}

const formatTimestamp = (value: string | null): string => {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "-";
  return format(date, "dd/MM/yyyy HH:mm");
};

const Field: React.FC<{
  icon: React.ReactNode;
  label: string;
  value: string;
}> = ({ icon, label, value }) => (
  <div className="flex items-start gap-2">
    <span className="text-gray-400 mt-0.5">{icon}</span>
    <div>
      <p className="text-xs text-gray-500">{label}</p>
      <p className="text-sm font-medium text-[#402430]">{value}</p>
    </div>
  </div>
);

export const NsuMoreInfoContent: React.FC<NsuMoreInfoContentProps> = ({ nsu }) => {
  const [fetchVarDetails, { isLoading }] = useFetchNsuVarDetailsMutation();
  const [details, setDetails] = useState<INsuVarDetails>({
    operatorName: nsu.operator_name,
    lastUpdatedAt: nsu.var_last_updated_at,
  });
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const isEmpty = !details.operatorName && !details.lastUpdatedAt;
  const isOperatorMissing = !details.operatorName && !!details.lastUpdatedAt;

  const handleFetch = async () => {
    setErrorMessage(null);

    try {
      const result = await fetchVarDetails({ nsuId: nsu.id }).unwrap();
      setDetails(result);
      const found = Boolean(result.operatorName || result.lastUpdatedAt);
      toast(
        found ? MORE_INFO_MESSAGES.FETCH.SUCCESS : MORE_INFO_MESSAGES.FETCH.EMPTY,
      );
    } catch (error) {
      const status = (error as { status?: number })?.status;
      if (status === 401 || status === 403 || status === 423) {
        return;
      }
      const data = (
        error as { data?: { message?: string; messages?: string[] } }
      )?.data;
      setErrorMessage(
        data?.messages?.[0] ??
          data?.message ??
          MORE_INFO_LABELS.FETCH_ERROR_DEFAULT,
      );
    }
  };

  return (
    <div className="flex flex-col gap-4 p-1 min-w-[320px]">
      <div className="flex flex-col gap-3">
        <Field
          icon={<User className="w-4 h-4" />}
          label={MORE_INFO_LABELS.OPERATOR_NAME}
          value={details.operatorName ?? "-"}
        />
        <Field
          icon={<Calendar className="w-4 h-4" />}
          label={MORE_INFO_LABELS.LAST_UPDATED}
          value={formatTimestamp(details.lastUpdatedAt)}
        />
      </div>

      {isOperatorMissing && (
        <p className="text-xs text-muted-foreground">
          {MORE_INFO_LABELS.OPERATOR_NOT_REGISTERED}
        </p>
      )}

      {isEmpty && (
        <div className="flex flex-col gap-3 border-t pt-3">
          <p className="text-xs text-muted-foreground">
            {MORE_INFO_LABELS.EMPTY_STATE}
          </p>
          <Button
            variant="outline"
            size="sm"
            onClick={handleFetch}
            disabled={isLoading}
            className="self-start"
          >
            {isLoading ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <Search className="w-4 h-4 mr-2" />
            )}
            {isLoading ? MORE_INFO_LABELS.FETCHING : MORE_INFO_LABELS.FETCH_ACTION}
          </Button>
        </div>
      )}

      {errorMessage && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{errorMessage}</AlertDescription>
        </Alert>
      )}
    </div>
  );
};
